import type { MapDataGameCoordinateDeltas, MapDataGameCoordinates } from './map-types';

const EARTH_RADIUS_METERS = 6_371_000;

export interface LngLatPoint {
    readonly lat: number;
    readonly lng: number;
}

export interface MapPlaneVector {
    readonly east: number;
    readonly north: number;
}

export function hasValidGameCoordinateDeltas(deltas: MapDataGameCoordinateDeltas): boolean {
    return (
        Number.isFinite(deltas.scaleX) &&
        Number.isFinite(deltas.scaleY) &&
        Number.isFinite(deltas.offsetX) &&
        Number.isFinite(deltas.offsetY) &&
        deltas.scaleX !== 0 &&
        deltas.scaleY !== 0 &&
        deltas.gameCoordsMap.x !== deltas.gameCoordsMap.y
    );
}

export function hasIndependentGameCoordinateAxes(gameCoordsMap: MapDataGameCoordinateDeltas['gameCoordsMap']): boolean {
    return new Set([gameCoordsMap.x, gameCoordsMap.y, gameCoordsMap.z]).size === 3;
}

export function remapGameCoordinates(
    coords: MapDataGameCoordinates,
    gameCoordsMap: MapDataGameCoordinateDeltas['gameCoordsMap'],
): MapDataGameCoordinates {
    return {
        x: coords[gameCoordsMap.x],
        y: coords[gameCoordsMap.y],
        z: coords[gameCoordsMap.z],
        metadata: coords.metadata,
    };
}

export function gameDirectionToBearing(
    direction: MapDataGameCoordinates,
    deltas: MapDataGameCoordinateDeltas,
): number | null {
    const mapDirection = gameDirectionToMapPlane(direction, deltas);
    if (!mapDirection || (mapDirection.east === 0 && mapDirection.north === 0)) return null;

    return ((Math.atan2(mapDirection.east, mapDirection.north) * 180) / Math.PI + 360) % 360;
}

export function gameDirectionToMapPlane(
    direction: MapDataGameCoordinates,
    deltas: MapDataGameCoordinateDeltas,
): MapPlaneVector | null {
    return remappedGameDirectionToMapPlane(remapGameCoordinates(direction, deltas.gameCoordsMap), deltas);
}

export function remappedGameDirectionToMapPlane(
    direction: Pick<MapDataGameCoordinates, 'x' | 'y'>,
    deltas: Pick<MapDataGameCoordinateDeltas, 'scaleX' | 'scaleY'>,
): MapPlaneVector | null {
    if (!Number.isFinite(direction.x) || !Number.isFinite(direction.y) || !hasValidGameCoordinateScale(deltas)) {
        return null;
    }

    const east = direction.x / deltas.scaleX;
    const north = direction.y / deltas.scaleY;

    return Number.isFinite(east) && Number.isFinite(north) ? { east, north } : null;
}

export function getRemappedGamePlaneHandedness(
    deltas: Pick<MapDataGameCoordinateDeltas, 'scaleX' | 'scaleY'>,
): 1 | -1 | null {
    if (!hasValidGameCoordinateScale(deltas)) {
        return null;
    }

    return deltas.scaleX * deltas.scaleY < 0 ? -1 : 1;
}

export function gameCoordinatesToLngLat(
    coords: MapDataGameCoordinates,
    deltas: MapDataGameCoordinateDeltas,
): LngLatPoint | null {
    if (!hasValidGameCoordinateDeltas(deltas)) {
        return null;
    }

    const adjustedX = (coords.x - deltas.offsetX) / deltas.scaleX;
    const adjustedY = (coords.y - deltas.offsetY) / deltas.scaleY;
    const lng = ((adjustedX / EARTH_RADIUS_METERS) * 180) / Math.PI;
    const lat = (Math.atan(Math.sinh(adjustedY / EARTH_RADIUS_METERS)) * 180) / Math.PI;

    return Number.isFinite(lng) && Number.isFinite(lat) && Math.abs(lng) <= 180 && Math.abs(lat) <= 90
        ? { lng, lat }
        : null;
}

export function lngLatToGameCoordinates(
    point: LngLatPoint,
    deltas: MapDataGameCoordinateDeltas,
    z = 0,
): MapDataGameCoordinates | null {
    if (
        !Number.isFinite(point.lng) ||
        !Number.isFinite(point.lat) ||
        Math.abs(point.lng) > 180 ||
        Math.abs(point.lat) > 90 ||
        !hasValidGameCoordinateDeltas(deltas) ||
        !Number.isFinite(z)
    ) {
        return null;
    }

    const lngRadians = (point.lng * Math.PI) / 180;
    const latRadians = (point.lat * Math.PI) / 180;
    const mercatorX = EARTH_RADIUS_METERS * lngRadians;
    const mercatorY = EARTH_RADIUS_METERS * Math.log(Math.tan(Math.PI / 4 + latRadians / 2));
    const x = mercatorX * deltas.scaleX + deltas.offsetX;
    const y = mercatorY * deltas.scaleY + deltas.offsetY;

    return Number.isFinite(x) && Number.isFinite(y) ? { x, y, z } : null;
}

function hasValidGameCoordinateScale(deltas: Pick<MapDataGameCoordinateDeltas, 'scaleX' | 'scaleY'>): boolean {
    return (
        Number.isFinite(deltas.scaleX) && Number.isFinite(deltas.scaleY) && deltas.scaleX !== 0 && deltas.scaleY !== 0
    );
}

export function getLngLatDistanceMeters(from: LngLatPoint, to: LngLatPoint): number {
    const fromLat = (from.lat * Math.PI) / 180;
    const toLat = (to.lat * Math.PI) / 180;
    const deltaLat = ((to.lat - from.lat) * Math.PI) / 180;
    const deltaLng = ((to.lng - from.lng) * Math.PI) / 180;
    const a = Math.sin(deltaLat / 2) ** 2 + Math.cos(fromLat) * Math.cos(toLat) * Math.sin(deltaLng / 2) ** 2;
    const clampedA = Math.min(Math.max(a, 0), 1);

    return EARTH_RADIUS_METERS * 2 * Math.atan2(Math.sqrt(clampedA), Math.sqrt(1 - clampedA));
}

export function getLngLatBearing(from: LngLatPoint, to: LngLatPoint): number {
    const fromLat = (from.lat * Math.PI) / 180;
    const toLat = (to.lat * Math.PI) / 180;
    const deltaLng = ((to.lng - from.lng) * Math.PI) / 180;
    const y = Math.sin(deltaLng) * Math.cos(toLat);
    const x = Math.cos(fromLat) * Math.sin(toLat) - Math.sin(fromLat) * Math.cos(toLat) * Math.cos(deltaLng);

    return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}
